import queryString from 'query-string';

import { getLogoutAbortController } from '@frontend/api';
import { fetchAndTrack } from '@frontend/utils';

import { API_URL } from 'constants/all';

import filterUndefined from './filterUndefined';
import { isObject } from './helpers';
import { keysToCamelCaseDeep, keysToSnakeCaseDeep } from './stringUtils';
import { getToken } from './token';

function prepareFetch(
  url,
  token,
  params,
  options,
  additionalRequestParams = {},
) {
  let fetchUrl = url.indexOf('://') === -1 ? API_URL + url : url;
  //For development from localhost against production APIs, uncomment:
  //* This is necessary for `TARGET_ENV="localAgainstProduction" yarn dev` because localhost is http and we require requests to api.paperlessparts.com to be https
  //* But in other cases we want the protocol to match for the static js/css/html and the API requests
  //if (fetchUrl.includes('http:')) {
  //  fetchUrl = fetchUrl.replace('http', 'https');
  //}
  let credentials = {};
  if (fetchUrl.includes(API_URL)) {
    // if we are making  CORS request to our API server, send any cookies
    credentials = {
      credentials: 'include',
    };
  }

  let headers = {
    Accept: 'application/json',
  };

  if (token !== null) {
    headers.Authorization = 'Token ' + token;
  }

  if ('headers' in additionalRequestParams) {
    headers = {
      ...headers,
      ...additionalRequestParams.headers,
    };
  }

  const fetchOptions = Object.assign(
    {},
    {
      method: 'GET',
      headers: headers,
    },
    credentials,
    options,
  );

  if ('signal' in additionalRequestParams) {
    fetchOptions['signal'] = additionalRequestParams['signal'];
  } else {
    const logoutAbortController = getLogoutAbortController();
    fetchOptions['signal'] = logoutAbortController.signal;
  }

  //If a fetchProgress controller (FetchProgressController) is provided, it will be used to
  //track start of request, start of response, and request error (if applicable).
  //* Note if response.json(), response.text(), etc throws, you still need to `catch` and handle
  //  it as always.  But you don't need to inform the fetchProgress controller, because the fetchProgress
  //  controller will observe the error too.  And it will notify its progressSignal's observers.
  //  See {FetchProgressController} in '@frontend/controllers'
  if ('fetchProgress' in additionalRequestParams) {
    fetchOptions['fetchProgress'] = additionalRequestParams['fetchProgress'];
  }

  if (params instanceof FormData) {
    fetchOptions.body = params;
  } else if (fetchOptions.method === 'GET') {
    const query =
      params &&
      queryString.stringify(keysToSnakeCaseDeep(filterUndefined(params)), {
        arrayFormat: 'comma',
      });

    if (query) {
      fetchUrl = [fetchUrl, query].join('?');
    }
  } else {
    headers['Content-Type'] = 'application/json';
    fetchOptions.body =
      process.env.IGNORE_SNAKE_CASE_CONVERSION ||
      additionalRequestParams.ignoreSnakeCaseConversion
        ? JSON.stringify(params)
        : JSON.stringify(keysToSnakeCaseDeep(params));
  }

  return { fetchUrl, fetchOptions };
}

const getPayload = (response) => {
  const contentType = response.headers.get('Content-Type');
  const isImage = contentType && contentType.indexOf('image') !== -1;
  const isJSON = contentType && contentType.indexOf('application/json') !== -1;
  const isZip = contentType && contentType.indexOf('application/zip') !== -1;

  if (isImage || isZip) {
    return response.blob();
  }
  if (isJSON) {
    return response.json().then(keysToCamelCaseDeep);
  }
  return response.text();
};

export function FetchError(statusCode, payload) {
  this.name = 'FetchError';
  this.stack = new Error().stack;
  this.statusCode = statusCode;
  if (statusCode) {
    this.originalResponse = payload;
  }
  if (isObject(payload)) {
    this.code = payload.code;
    this.message = payload.message;
    this.extra = payload.extra;
  } else if (statusCode) {
    if (statusCode === 502 || statusCode === 503) {
      this.message = 'Connection unavailable.';
    } else {
      this.message = 'Unknown error.';
    }
  } else {
    this.message = 'Server is unavailable.';
  }
}
FetchError.prototype = Object.create(Error.prototype);
FetchError.prototype.constructor = FetchError;

const request = async (url, params, options, additionalRequestParams) => {
  const token = getToken();
  const args = prepareFetch(
    url,
    token,
    params,
    options,
    additionalRequestParams,
  );

  let response;
  try {
    response = await fetchAndTrack(args.fetchUrl, args.fetchOptions);
  } catch (error) {
    if (error?.message) {
      //Log message to help with debugging
      //If an error throws, it is likely to do with how fetch() (fetchAndTrack) is called
      //* Other fetch errors will be indicated in the resolved response object.
      console.error(error?.message);
    }
    throw new FetchError(null, null);
  }

  const statusCode = response.status;
  const payload = await getPayload(response);

  const method = options?.method ? options.method.toUpperCase() : 'GET';
  const currentLocation = window.location.pathname;
  if (statusCode === 401 && !currentLocation.includes('login')) {
    console.error(
      `Error ${method} ${url}: (Status: 401). Redirecting to login`,
    );
    const initialPage = currentLocation + window.location.search;
    window.location.replace(
      `${window.location.origin}/login?next=${initialPage}`,
    );
  }

  if (statusCode === 402) {
    console.error(
      `Error ${method} ${url}: (Status: 402). Redirecting to login`,
    );
    window.location.replace(`${window.location.origin}/payment-required`);
  }

  if (statusCode >= 400 || (isObject(payload) && payload.error === true)) {
    throw new FetchError(statusCode, payload);
  }

  return payload;
};

for (const method of ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']) {
  request[method.toLowerCase()] = (
    url,
    params = {},
    additionalRequestParams = {},
  ) => {
    return request(url, params, { method }, additionalRequestParams);
  };
}

export default request;
